import { memo } from "react"
import { AudioLines, Eye, EyeOff, Mic, Trash2, Type, Upload } from "lucide-react"
import { cn } from "@/lib/utils"
import { useTranslation } from "@/hooks/use-translation"
import { isAnimaticTrack } from "@/lib/timeline"
import { VersionNameInlineEdit } from "@/components/shot-panel/version-name-inline-edit"
import { TrackVolumeControl } from "./track-volume-control"
import type { TimelineTrack, TimelineVersionOption } from "./types"

type Props = {
  track: TimelineTrack
  /** Порядковый номер видеодорожки (V1 — верхняя), fallback если нет версии. */
  videoIndex?: number
  version?: TimelineVersionOption | null
  /** Порядковый номер версии шота (v1/a1 — самая ранняя по id). */
  versionOrdinal?: number
  canDelete: boolean
  /** Единый патч звука дорожки: громкость 0..1 + mute (жесты контрола связывают их). */
  onTrackAudioChange: (trackId: string, volume: number, muted: boolean) => void
  /** Только video: «глаз» — включить/исключить слой. */
  onToggleHidden?: (trackId: string) => void
  onDetachAudio?: (trackId: string) => void
  onDelete: (trackId: string) => void
  /** Переименование версии шота (видеодорожки). */
  onRenameVersion?: (versionId: number, name: string) => void
  /** custom_audio: открыть инлайн-контролы записи/TTS/загрузки. */
  onOpenRecord?: (trackId: string) => void
  onOpenTts?: (trackId: string) => void
  onUpload?: (trackId: string) => void
}

function headerButton(title: string, onClick: () => void, icon: React.ReactNode, active = false, disabled = false) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "rounded p-1 transition-colors disabled:cursor-not-allowed disabled:opacity-40",
        active
          ? "bg-ui-surface-raised text-ui-accent-text"
          : "text-ui-text-secondary hover:bg-ui-surface-raised hover:text-ui-text-primary",
      )}
    >
      {icon}
    </button>
  )
}

/**
 * Шапка дорожки: единый двухстрочный макет — название сверху, кнопки снизу.
 * Для видеодорожек название версии редактируется инлайн.
 */
export const TrackHeader = memo(function TrackHeader({
  track,
  videoIndex,
  version,
  versionOrdinal,
  canDelete,
  onTrackAudioChange,
  onToggleHidden,
  onDetachAudio,
  onDelete,
  onRenameVersion,
  onOpenRecord,
  onOpenTts,
  onUpload,
}: Props) {
  const { t } = useTranslation(["shot"])

  const animatic = isAnimaticTrack(track)
  const versionName = version?.name ?? (videoIndex != null ? `v${videoIndex}` : null)
  const label = animatic
    ? t("timeline.animaticTrackLabel")
    : track.kind === "video"
      ? (versionName ?? `v${videoIndex ?? 1}`)
      : track.kind === "version_audio"
        ? versionOrdinal != null
          ? t("timeline.versionAudioNamed", { n: versionOrdinal })
          : t("timeline.versionAudioLabel")
        : t("timeline.customAudioLabel")

  const canRename = track.kind === "video" && !animatic && version != null && onRenameVersion != null
  const hideVolume = (track.kind === "video" && track.audio_detached) || track.animatic_mode === "video_silent"
  const isCorrection = track.purpose === "correction"

  return (
    <div
      className={cn(
        "flex h-full w-[136px] shrink-0 flex-col justify-center gap-0.5 overflow-hidden rounded-md px-1.5 py-1",
        isCorrection
          ? "border border-indigo-500/60 bg-indigo-500/35 text-indigo-900 dark:bg-indigo-500/25 dark:text-indigo-200"
          : "bg-ui-surface-sunken",
      )}
      title={isCorrection ? t("timeline.correctionTrackHint") : undefined}
    >
      {canRename && version ? (
        // Обёртка-строка: кнопка имени сжимается по содержимому — ховер и клик
        // работают только на самом названии, а не на всей ширине шапки.
        <div className="flex min-w-0 items-center gap-1">
          <VersionNameInlineEdit
            name={version.name}
            label={label ?? ""}
            title={t("timeline.renameVersion")}
            onCommit={(next) => onRenameVersion?.(version.id, next)}
            className="max-w-full"
          />
        </div>
      ) : (
        <div className="flex min-w-0 items-center gap-1 px-1">
          <div
            className={cn("truncate text-[10px] font-semibold", isCorrection ? "text-inherit" : "text-ui-text-primary")}
            title={label ?? undefined}
          >
            {label}
          </div>
        </div>
      )}
      <div className="flex">
        {track.kind === "video" && onToggleHidden
          ? headerButton(
              track.hidden ? t("timeline.showTrack") : t("timeline.hideTrack"),
              () => onToggleHidden(track.id),
              track.hidden ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />,
              track.hidden === true,
            )
          : null}
        {/* На видеодорожке с откреплённым аудио и на немом аниматике звука нет. */}
        {hideVolume ? null : <TrackVolumeControl track={track} onTrackAudioChange={onTrackAudioChange} />}
        {track.kind === "video" && !animatic && onDetachAudio
          ? headerButton(
              track.audio_detached ? t("timeline.audioDetached") : t("timeline.detachAudio"),
              () => onDetachAudio(track.id),
              <AudioLines className="h-3 w-3" />,
              track.audio_detached === true,
              track.audio_detached === true,
            )
          : null}
        {track.kind === "custom_audio" && !animatic && onOpenRecord
          ? headerButton(t("timeline.record"), () => onOpenRecord(track.id), <Mic className="h-3 w-3" />)
          : null}
        {track.kind === "custom_audio" && !animatic && onOpenTts
          ? headerButton(t("timeline.tts"), () => onOpenTts(track.id), <Type className="h-3 w-3" />)
          : null}
        {track.kind === "custom_audio" && !animatic && onUpload
          ? headerButton(t("timeline.uploadAudio"), () => onUpload(track.id), <Upload className="h-3 w-3" />)
          : null}
        {canDelete
          ? headerButton(t("timeline.deleteTrack"), () => onDelete(track.id), <Trash2 className="h-3 w-3" />)
          : null}
      </div>
    </div>
  )
})
